// What was said so far in a meeting that is still recording (BUILD 1 WP-4,
// MAP-MEETINGS §5; addendum A4).
//
// On the recording device: the live preview from the recorder store, with
// the composer dictation's text treatment (final lines in the normal text
// colour, the current partial greyed until it is final), a lag badge when the
// provider falls more than 5 s behind and "N s not shown live" after a
// reconnect. Anywhere else (another device, or after a reload): the
// provisional live rows the server persisted from the preview, polled from
// GET meetings/:id/live. Both say plainly that the final transcript comes
// from the recording. There is no language control: engines detect the
// spoken language. Rows, cursor and speaker colours belong to one meeting.
// After Stop the finishing view (meeting-finishing.tsx, PL-30) reads the same
// rows through useMeetingLiveRows, so the text stays on screen while the
// recording is transcribed. Owner: WP-4.
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import meetingsLiveCopy from '@/i18n/ns/meetings-live';
import meetingsRecordCopy from '@/i18n/ns/meetings-record';
import { formatClock } from './meeting-record-parts';
import { recorderElapsedMs, useMeetingRecorder } from './meeting-recorder';
import type { PreviewLine } from './meeting-recorder-preview';
import { meetingsApi } from './meetings-api';
import type { CompanyMeeting, MeetingLiveSegment } from './types';

const LAG_BADGE_MS = 5000;
const LIVE_POLL_MS = 3000;
const LIVE_ROWS_KEPT = 2000;

/** A provisional colour per preview speaker label, by first appearance (S9). */
function speakerTone(order: Map<string, number>, speaker: string | null) {
  if (!speaker) return undefined;
  if (!order.has(speaker)) order.set(speaker, order.size % 6);
  return String(order.get(speaker));
}

/** One line: a final (normal text) or the partial (greyed, not announced). */
function LiveLine({
  atMs,
  text,
  tone,
  final = true,
}: {
  atMs: number;
  text: string;
  tone?: string;
  final?: boolean;
}) {
  return (
    <p
      className={`ws-live-line${final ? '' : ' partial'}`}
      data-speaker-tone={tone}
      aria-hidden={final ? undefined : true}
    >
      <span className="ws-live-time">{formatClock(atMs)}</span>
      <span data-final={final}>{text}</span>
    </p>
  );
}

function LiveScroll({ children, count }: { children: ReactNode; count: number }) {
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const element = box.current;
    if (element) element.scrollTop = element.scrollHeight;
  }, [count]);
  return (
    <div className="ws-live-lines" ref={box} role="log" aria-live="polite">
      {children}
    </div>
  );
}

function PreviewStatus({ lastLine }: { lastLine: PreviewLine | undefined }) {
  const copy = useNamespaceCopy(meetingsRecordCopy);
  const state = useMeetingRecorder((snapshot) => snapshot.preview.state);
  const notShownMs = useMeetingRecorder((snapshot) => snapshot.preview.notShownMs);
  const elapsed = useMeetingRecorder(recorderElapsedMs);
  const lag = lastLine ? elapsed - lastLine.endAtMs : 0;
  return (
    <div className="ws-live-status">
      {state === 'paused' && <span>{copy.previewPaused}</span>}
      {state === 'unavailable' && <span>{copy.previewUnavailable}</span>}
      {state === 'live' && lag > LAG_BADGE_MS && (
        <span className="ws-live-badge">
          {formatCopy(copy.previewLag, { seconds: Math.round(lag / 1000) })}
        </span>
      )}
      {notShownMs >= 1000 && (
        <span>{formatCopy(copy.previewNotShown, { seconds: Math.round(notShownMs / 1000) })}</span>
      )}
    </div>
  );
}

/** The live preview of this device's recording (one mount per meeting). */
export function MeetingLivePreview({ meeting }: { meeting: CompanyMeeting }) {
  return <LivePreview key={meeting.id} />;
}

function LivePreview() {
  const copy = useNamespaceCopy(meetingsRecordCopy);
  const live = useNamespaceCopy(meetingsLiveCopy);
  const lines = useMeetingRecorder((state) => state.preview.lines);
  const partial = useMeetingRecorder((state) => state.preview.partial);
  const tones = useRef(new Map<string, number>()).current;
  return (
    <section className="ws-live-panel" aria-label={live.liveTranscriptTitle}>
      <header>
        <h2>{live.liveTranscriptTitle}</h2>
      </header>
      <p className="ws-page-note">{copy.previewCaption}</p>
      <PreviewStatus lastLine={lines.at(-1)} />
      <LiveScroll count={lines.length + (partial ? 1 : 0)}>
        {lines.map((line) => (
          <LiveLine
            key={line.key}
            atMs={line.atMs}
            text={line.text}
            tone={speakerTone(tones, line.speaker)}
          />
        ))}
        {partial && <LiveLine atMs={partial.atMs} text={partial.text} final={false} />}
        {!lines.length && !partial && <p className="ws-muted">{live.noLiveTextYet}</p>}
      </LiveScroll>
    </section>
  );
}

/**
 * Provisional live rows persisted by the server (another device, or after a
 * reload). Keyed by meeting: switching to another recording meeting starts
 * from its own first row, never with the previous meeting's rows or cursor.
 */
export function MeetingLiveRows({ meeting }: { meeting: CompanyMeeting }) {
  return <LiveRows key={meeting.id} meeting={meeting} />;
}

// The rows read so far, per meeting, so a view that replaces another one (the
// capture view by the finishing view after Stop) starts from them instead of
// an empty list. A few meetings at most; the oldest is dropped first.
const LIVE_CACHE_MEETINGS = 3;
type LiveCache = { rows: MeetingLiveSegment[]; cursor: string | null; loaded: boolean };
const liveCache = new Map<string, LiveCache>();
function remember(meetingId: string, entry: LiveCache) {
  liveCache.delete(meetingId);
  liveCache.set(meetingId, entry);
  while (liveCache.size > LIVE_CACHE_MEETINGS) liveCache.delete(liveCache.keys().next().value!);
}

/** New rows after the ones already shown; a row read twice (same session and segment) once. */
function appendRows(current: MeetingLiveSegment[], incoming: MeetingLiveSegment[]) {
  const seen = new Set(current.map((row) => `${row.previewSessionId}:${row.seg}`));
  const fresh = incoming.filter((row) => !seen.has(`${row.previewSessionId}:${row.seg}`));
  return fresh.length ? [...current, ...fresh].slice(-LIVE_ROWS_KEPT) : current;
}

/**
 * A meeting's provisional live rows: read once, then every 3 s while
 * `polling`. `loaded` turns true after the first answer (or a cached one).
 * Mount it keyed by meeting so rows and cursor never cross meetings.
 */
export function useMeetingLiveRows(meetingId: string, polling: boolean) {
  const cached = liveCache.get(meetingId);
  const [rows, setRows] = useState<MeetingLiveSegment[]>(cached?.rows ?? []);
  const [loaded, setLoaded] = useState(cached?.loaded ?? false);
  const [failed, setFailed] = useState(false);
  const cursor = useRef<string | null>(cached?.cursor ?? null);
  useEffect(() => {
    let active = true;
    let timer = 0;
    const poll = async () => {
      try {
        const page = await meetingsApi.liveSegments(meetingId, {
          cursor: cursor.current,
          limit: 500,
        });
        if (!active) return;
        cursor.current = page.cursor;
        // Read here, inside the try: a malformed page marks the rows failed.
        const incoming = page.rows;
        const fresh = incoming.length > 0;
        setRows((current) => {
          const next = fresh ? appendRows(current, incoming) : current;
          remember(meetingId, { rows: next, cursor: cursor.current, loaded: true });
          return next;
        });
        setLoaded(true);
        setFailed(false);
      } catch {
        if (active) setFailed(true);
      }
      if (active && polling) timer = window.setTimeout(() => void poll(), LIVE_POLL_MS);
    };
    void poll();
    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [meetingId, polling]);
  return { rows, loaded, failed };
}

function LiveRows({ meeting }: { meeting: CompanyMeeting }) {
  const live = useNamespaceCopy(meetingsLiveCopy);
  const { rows, failed } = useMeetingLiveRows(meeting.id, meeting.status === 'capturing');
  const tones = useRef(new Map<string, number>()).current;
  return (
    <section className="ws-live-panel" aria-label={live.liveTranscriptTitle}>
      <header>
        <h2>{live.liveTranscriptTitle}</h2>
        <span className="ws-live-badge">{live.provisional}</span>
      </header>
      <p className="ws-page-note">{live.provisionalNote}</p>
      {!!rows.length && (
        <p className="ws-muted">{formatCopy(live.liveRowsCount, { count: rows.length })}</p>
      )}
      <LiveScroll count={rows.length}>
        {rows.map((row) => (
          <LiveLine
            key={`${row.previewSessionId}:${row.seg}`}
            atMs={row.atMs}
            text={row.text}
            tone={speakerTone(tones, row.speaker)}
          />
        ))}
        {!rows.length && (
          <p className="ws-muted">{failed ? live.liveUnavailable : live.noLiveTextYet}</p>
        )}
      </LiveScroll>
    </section>
  );
}
